import { memo, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { cn, Price } from '@divlab/divanui';
import { IconChevronRight, IconEmptyMessage } from '@divlab/divanui/icons';

import { isInViewport } from '@Utils/isInViewport';
import * as DeliveryTerms from '@Queries/DeliveryTerms';
import * as ProductSellPoints from '@Queries/ProductSellPoints';
import { useRelatedProducts } from '@Queries/RelatedProducts';
import * as Profile from '@Queries/Profile';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import * as HeaderStore from '@Stores/Header';
import useNavigation from '@Navigation/useNavigation';
import { useRequest } from '@Contexts/Request';
import { useDeps } from '@Contexts/DI';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import { useUpdateComponentStorage } from '@Stores/ComponentStorage';
import getProductName from '@Utils/getProductName';
import ProductLike from '@Components/ProductLike';
import Fabrics from '@Components/Fabrics';
import SmartRender from '@Components/SmartRender';
import AsyncOrderBonuses from '@Components/AsyncOrderBonuses';
import FullСashbackTag from '@Components/FullCashbackTag';
import { useMeta } from '@Queries/Meta';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import Link from '@UI/Link';
import OnSaleSoon from '@Pages/PageProduct/elements/OnSaleSoon';
import AboutReceiptButton from '@Components/AboutReceiptButton';
import YandexSplitBadge from '@Components/YandexSplitBadge';
import YandexSplitWidget from '@Components/YandexSplitWidget';
import LinksList from '../LinksList';
import BuyButton from '../BuyButton';
import BuyLink from '../BuyLink';
import PreorderButton from '../PreorderButton';
import RelatedProductsList from '../RelatedProductsList';
import OutOfStock from '../OutOfStock';
import Sizes from '../Sizes';
import ProductOptions from '../ProductOptions';
import DefectDescription from './elements/DefectDescription';
import CreditTabWrapper from './elements/CreditTabWrapper';
import PreorderInfoLink from './elements/PreorderInfoLink';
import StickyBuyButton from '../StickyBuyButton';
import CheaperProduct from './elements/CheaperProduct';
import DefaultPrices from './elements/DefaultPrices';
import PromoCodePrices from './elements/PromoCodePrices';
import styles from './Sidebar.module.css';

import type { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react';
import type { ColorData } from '@Types/Product';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  goodsInfo?: PecomDeliveryParameters[];
  onClickCharacteristics?: (e: MouseEvent) => void;
  onClickReviews?: (e: MouseEvent) => void;
  onClickAtr?: (e: MouseEvent) => void;
}

export interface ContainerProps extends PropsWithChildren {
  className?: string;
  scrollable?: boolean;
  cnLayer?: string;
}

const Sidebar: FC<SidebarProps> = (props) => {
  const { className, onClickCharacteristics, onClickReviews, onClickAtr, goodsInfo, ...restProps } =
    props;
  const page = PageProductStore.useData();
  const storeModules = PageProductStore.useModules();
  const { t } = useTranslation();
  const { pathname } = useNavigation();
  const { data: relatedProducts } = useRelatedProducts(page.product.id);
  const countOfChangedOptions = PageProductStore.getCountOfChangedParameters();
  const { data: meta } = useMeta();
  const { region } = meta;
  const changeableParameters = PageProductStore.useChangeableParameters();
  const sizeParameter = PageProductStore.useSizeParameter();
  const isAtr = page.isAtr;
  const hasReadySolutions = page.readySolutions?.colors?.length > 0;
  const { personalAreaAvailable } = useFeatureFlags();
  const { isRus } = useRequest();
  const isRibble = page.classifications?.includes('ribble');
  const isBrera = page.classifications?.includes('brera');
  const { totalHeight } = HeaderStore.useSizes();
  const refBuyButton = useRef<HTMLDivElement>(null);
  const [buttonInView, setButtonInView] = useState(false);
  const deliveryPrice = parseInt(page.deliveryPage.price) || null;
  const profile = Profile.useProfile();
  const isExtraBonus = page.product.extraBonus;
  const isClientDesigner = profile.data?.clientType === 'designer';
  const isBonusesShown = page.isAvailable && !isClientDesigner && personalAreaAvailable;
  const promocodeCheck = isRus ? !page.promoCode : true;
  const { analytics } = useDeps();
  const textOutOfStock = !page.lorenaKimbolBanner ? t('unavailable') : t('permanently-unavailable');
  const modules = storeModules.map((module) => ({
    id: module.id,
    quantity: module.count,
  }));

  // TODO: Временное решение, удалить при переходе на страницу с новым конструктором для шкафов
  const wardrobesCategoryIds = [42, 4, 41, 2295, 448, 39];
  const isWardrobes = wardrobesCategoryIds.includes(page.categoryId);
  const isComingSoon = page.product.status === 'ComingSoon';

  const deliveryTerms = DeliveryTerms.useDeliveryTerms({
    productId: page.product.id,
  });

  const showrooms = ProductSellPoints.useProductSellPoints({
    productId: page.product.id,
  });

  const additional = useMemo(() => {
    const products = relatedProducts?.products || [];

    return products
      .filter((product) => product.quantity > 0)
      .map((product) => ({
        id: product.id,
        quantity: product.quantity,
      }));
  }, [relatedProducts]);

  const deliveryDate = useMemo(() => {
    if (!deliveryTerms.data) return null;

    return deliveryTerms.data[0];
  }, [deliveryTerms.data]);

  const showDeliveryDate = deliveryDate && !countOfChangedOptions;

  const mainCredit = useMemo(() => {
    if (!page.credit) return null;

    const availableCredit = page.credit.find((credit) => credit.isMain);

    if (availableCredit) {
      return availableCredit;
    }

    return page.credit.find((credit) => credit.creditAvailable);
  }, [page.credit]);

  const defaultSelectedFabric = useMemo(() => {
    return page.readySolutions?.colors?.find((color) => color.shopProductId === page.product.id);
  }, [page.readySolutions, page.product.id]);

  const countReviewsText = useMemo(() => {
    const reviews = (page.reviewsSubgallery || []).filter((review) => {
      return review.source === 'site';
    });

    const label = t('plural-reviews', reviews.length);

    return `${reviews.length} ${label}`;
  }, [page, t]);

  const handleClickShowroom = useCallback(() => {
    ModalWindows.open('Showrooms', { showrooms: showrooms.sellPoints });
  }, [showrooms?.sellPoints]);

  const handleClickFinalPrice = useCallback(() => {
    ModalWindows.open('FinalPrice');
  }, []);

  const handleClickWarranty = useCallback(async () => {
    ModalWindows.open('WarrantyProducts', {
      shopProductId: page.product.id,
      warranty: page.warranty,
    });
  }, [page.warranty, page.product.id]);

  const handleClickDeliveryInformation = useCallback(() => {
    analytics.dispatchEvent('delivery.click', { url: pathname });

    ModalWindows.open('DeliveryInformation', {
      delivery: page.deliveryPage,
      goodsInfo,
      deliveryDate: showDeliveryDate && deliveryDate,
    });
  }, [page.deliveryPage, goodsInfo, deliveryDate, showDeliveryDate, analytics, pathname]);

  const handleClickModuleConstructor = useCallback(() => {
    analytics.dispatchEvent('constructor.clickToOpen', {
      source: page.classifications[0] || 'ribble',
    });

    ModalWindows.open('ModuleConstructor', {
      modelId: page.product.modelId,
      fabricId: page.product.configurator?.fabricId,
      configurationId: page.product.configurator?.configurationId,
      code: page.classifications[0] || 'ribble',
      source: page.classifications[0] || 'ribble',
    });
  }, [analytics, page.classifications, page.product]);

  const handleClickConstructor = useCallback(
    (source: string) => {
      analytics.dispatchEvent('constructor.clickToOpen', {
        source,
      });

      if (isRibble || isBrera) {
        handleClickModuleConstructor();
        return;
      }

      ModalWindows.open('Constructor', {
        productId: page.product.modelId,
        source,
      });
    },
    [analytics, handleClickModuleConstructor, isBrera, isRibble, page.product.modelId],
  );

  const handleClickNotifyAboutReceipt = useCallback(() => {
    ModalWindows.open('NotifyAboutReceipt', {
      product: page.product,
      group: page.modules.length !== 0,
    });
  }, [page.modules, page.product]);

  const handleClickReviews = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      if (onClickReviews) onClickReviews(e);
    },
    [onClickReviews],
  );

  const handleClickAtr = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      if (onClickAtr) onClickAtr(e);
    },
    [onClickAtr],
  );

  const handleClickOrderSamples = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      ModalWindows.open('OrderTextileSamples', { id: page.product.id });
    },
    [page.product.id],
  );

  const handleClickOneClickBuy = useCallback(() => {
    ModalWindows.open('OneClickBuy', {
      product: { ...page.product, credit: mainCredit },
    });
  }, [mainCredit, page.product]);

  const handleClickColor = useCallback(
    (e: MouseEvent, clickedFabric: ColorData) => {
      if (clickedFabric === defaultSelectedFabric) return;

      analytics.dispatchEvent('readySolution.select', {
        category: 'ColorCard',
        name: page.product.name,
      });
    },
    [defaultSelectedFabric, page.product.name, analytics],
  );

  const handleScroll = useCallback(() => {
    if (!refBuyButton.current) return;
    // кнопка Купить во вьюпорте
    setButtonInView(() => {
      return isInViewport({ elem: refBuyButton.current, offset: totalHeight });
    });
  }, [totalHeight]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useUpdateComponentStorage({
    buyButton: page.isAvailable && <StickyBuyButton visible={!buttonInView} />,
  });

  return (
    <div {...restProps} className={cn(styles.sidebar, className)}>
      <div className={styles.shortName}>
        {page.product.model}
        <ProductLike
          className={styles.like}
          product={page.product}
          label='Карточка товара'
          data-testid='favorites-card'
        />
      </div>
      <h1 className={styles.fullName} itemProp='name'>
        {getProductName(page.product)}
      </h1>

      {isComingSoon ? (
        <div className={styles.soonWrapper}>
          <DefaultPrices className={styles.price} />

          <OnSaleSoon className={styles.soonBlock}>
            <AboutReceiptButton product={page.product} className={styles.soonButton} />
          </OnSaleSoon>
        </div>
      ) : (
        <>
          <div className={styles.wrapperRating}>
            <div className={styles.iconMessageWrapper}>
              <IconEmptyMessage />
            </div>
            <Link
              className={styles.countReviews}
              view='primary'
              underlined
              onClick={handleClickReviews}
              data-testid='sidebar-feedback'
            >
              {countReviewsText}
            </Link>
          </div>

          {isBonusesShown && (
            <div className={styles.bonusesWrapper}>
              {!isExtraBonus && (
                <AsyncOrderBonuses
                  productIds={[page.product.id]}
                  additional={additional}
                  module={modules}
                />
              )}
              {isExtraBonus && <FullСashbackTag />}
              {isRus && !region.isFranchise && (
                <YandexSplitBadge amount={String(page.product.price.actual)} />
              )}
            </div>
          )}

          {page.isAvailable ? (
            <>
              {page.promoCode ? (
                <PromoCodePrices className={styles.promoCodePrices} />
              ) : (
                <DefaultPrices className={styles.defaultPrices} />
              )}
              {!!mainCredit && promocodeCheck && (
                <CreditTabWrapper
                  className={styles.creditTab}
                  product={page.product}
                  pageCredit={mainCredit}
                  data-test-id='installment-cart'
                />
              )}
            </>
          ) : (
            <div className={styles.wrapperOutOfStock}>
              <OutOfStock text={textOutOfStock} />
            </div>
          )}

          {page.product.cheaperProduct && (
            <CheaperProduct
              product={page.product.cheaperProduct}
              className={styles.cheaperProduct}
            />
          )}

          {isAtr && (
            <div className={styles.promoInfo} onClick={handleClickAtr}>
              {t('atr.promo-info.1')}&nbsp;{t('atr.promo-info.2')}
              <span className={styles.promoInfoBold}>{t('atr.promo-info.atr')}</span>
              <Link className={styles.more} view='secondary'>
                {t('ui.more')}
                <IconChevronRight className={styles.moreIcon} />
              </Link>
            </div>
          )}

          {page.readySolutions && typeof page.readySolutions === 'object' && (
            <div className={styles.wrapperFabrics}>
              {hasReadySolutions && (
                <Fabrics
                  fabrics={page.readySolutions.colors}
                  count={page.readySolutions.configuratorCount}
                  defaultSelectedFabric={defaultSelectedFabric}
                  size='m'
                  onClickColor={handleClickColor}
                  onClickConstructor={() => handleClickConstructor('Карточка товара - цвет')}
                />
              )}

              {page.readySolutions.canOrderSamples && (
                <Link
                  className={cn(styles.orderFabrics, {
                    [styles.hasReadySolutions]: hasReadySolutions,
                  })}
                  view='secondary'
                  underlined
                  onClick={handleClickOrderSamples}
                  data-testid='textile-samples'
                >
                  {t('order-fabrics')}
                </Link>
              )}
            </div>
          )}

          {sizeParameter && <Sizes className={styles.sizes} parameter={sizeParameter} />}

          {changeableParameters.length > 0 && !page.lorenaKimbolBanner && (
            <ProductOptions className={styles.changebleOptionsMenuItem} />
          )}

          {page.relatedProducts?.length > 0 && (
            <RelatedProductsList
              className={styles.relatedProducts}
              forTitles={page.relatedProducts}
              mainId={page.product.id}
              warranty={page.warranty}
            />
          )}

          <div className={styles.actions}>
            {page.isAvailable ? (
              <>
                {page.product.isPreorder ? (
                  <>
                    <PreorderInfoLink className={styles.promoLink} productId={page.product.id} />
                    <PreorderButton className={styles.action} />
                    <BuyLink />
                  </>
                ) : (
                  <>
                    {page.isConfigurator &&
                      (isRibble || isBrera ? (
                        <div className={styles.action}>
                          <Button
                            wide
                            theme='secondary'
                            onClick={handleClickModuleConstructor}
                            data-testid='ribble-constructor-button'
                          >
                            {t('ribble.assembleSofa')}
                          </Button>
                        </div>
                      ) : (
                        <div className={styles.action}>
                          <Button
                            before={
                              isWardrobes &&
                              isRus && <div className={cn(styles.icon, styles.ruler)} />
                            }
                            wide
                            theme='secondary'
                            data-testid='link-to-constructor'
                            onClick={() => handleClickConstructor('Карточка товара')}
                          >
                            {isWardrobes && isRus
                              ? t('ui.change-size-and-color')
                              : t('ui.change-configuration')}
                          </Button>
                        </div>
                      ))}

                    {page.isDefect && (
                      <DefectDescription className={styles.defect} text={t('has-defects')} />
                    )}
                    <div className={styles.action} ref={refBuyButton}>
                      <BuyButton />
                    </div>

                    <SmartRender countries={['RUS', 'KAZ', 'UZB']}>
                      <Link
                        className={cn(styles.action, styles.actionLink)}
                        view='secondary'
                        underlined
                        onClick={handleClickOneClickBuy}
                        data-testid='buy-1-click'
                      >
                        {t('ui.one-click-buy')}
                      </Link>
                    </SmartRender>
                  </>
                )}
              </>
            ) : (
              <>
                {!page.lorenaKimbolBanner && (
                  <Button
                    className={styles.action}
                    wide
                    theme='secondary'
                    onClick={handleClickNotifyAboutReceipt}
                  >
                    {t('ui.notify-about-receipt')}
                  </Button>
                )}
              </>
            )}
          </div>

          {!!page.lorenaKimbolBanner && (
            <div className={styles.outOfStockBanner}>
              <div className={styles.bannerContent}>
                <div className={styles.bannerTitle}>{page.lorenaKimbolBanner.title}</div>
                <Link to={page.lorenaKimbolBanner.link}>
                  <Button className={styles.bannerLink}>{t('ui.go-to-product')}</Button>
                </Link>
              </div>
            </div>
          )}

          <div
            className={cn(styles.linksList, {
              [styles.withRelationBanner]: !!page.lorenaKimbolBanner,
            })}
          >
            {/*TODO: подставить данные с backend*/}
            <LinksList
              items={[
                (page.deliveryPage?.tables ||
                  page.deliveryPage?.deliveryTypes ||
                  page.deliveryPage?.delivery ||
                  page.deliveryPage?.teaser) &&
                  !page.lorenaKimbolBanner && {
                    testid: 'link-to-delivery-info',
                    icon: (
                      <div
                        className={cn(styles.icon, {
                          [styles.delivery]: isRus,
                          [styles.deliveryOld]: !isRus,
                        })}
                      />
                    ),
                    hasArrow: true,
                    label: page.deliveryPage.title,
                    addingText: deliveryPrice && !region.isFranchise && (
                      <div>
                        {t('deliver')} {showDeliveryDate && deliveryDate} {t('for')}{' '}
                        <Price price={deliveryPrice} />
                      </div>
                    ),
                    onClick: handleClickDeliveryInformation,
                  },
                page.product.price.isFinal && {
                  icon: <div className={cn(styles.icon, styles.attention)} />,
                  hasArrow: true,
                  label: t('links.price-final'),
                  onClick: handleClickFinalPrice,
                  testid: 'final-price',
                },
                page.warranty && {
                  icon: <div className={cn(styles.icon, styles.warranty)} />,
                  hasArrow: true,
                  label: page.warranty.sideBarTitle,
                  onClick: handleClickWarranty,
                },
                showrooms?.sellPoints?.length > 0 && {
                  icon: <div className={cn(styles.icon, styles.sellPoint)} />,
                  label: t('links.where-to-watch'),
                  hasArrow: true,
                  addingText: (
                    <div>
                      {t('model-is')} {showrooms.sellPoints.length}{' '}
                      {t('ui.plural-showrooms', showrooms.sellPoints.length)}
                    </div>
                  ),
                  onClick: handleClickShowroom,
                  testid: 'open-showrooms-modal',
                },
                {
                  label: t('characteristics'),
                  onClick: onClickCharacteristics,
                  testid: 'sidebar-characteristics',
                },
                {
                  label: t('links.reviews'),
                  onClick: onClickReviews,
                  testid: 'sidebar-feedback-and-photo',
                },
              ].filter(Boolean)}
            />
          </div>
          {isRus && mainCredit?.yandexSplitAvailable && mainCredit?.promoTag && (
            <YandexSplitWidget
              className={styles.yandexWidget}
              type='splitInfo'
              totalAmount={String(page.product.price.actual)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default memo(Sidebar);
